import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Award, Shield, Music, Briefcase, CheckCircle, Building2, Users, FileCheck, Globe } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

/* ═══════════════════════════════════════════════════════════════════════════════
 * ABOUT LEAVER HALL - SEO ANCHOR PAGE
 * URL: /about-leaver-hall
 * SEO Title: Leaver Hall - Founder & Managing Director | ItzGreen Music
 * Purpose: Google search authority for "Leaver Hall" → ItzGreen Music
 * ═══════════════════════════════════════════════════════════════════════════════ */

const AboutLeaverHallPage = () => {
  // Set page title for SEO
  React.useEffect(() => {
    document.title = "Leaver Hall - Founder & Managing Director | ItzGreen Music";
  }, []);

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/about"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO ABOUT
        </Link>

        {/* Executive Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            EXECUTIVE LEADERSHIP
          </Badge>

          {/* H1 - SEO Critical: Exact keyword match */}
          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
          >
            Leaver Hall | <span className="text-[rgb(218,255,1)]">Managing Director</span> & Founder, ItzGreen Music
          </h1>
        </motion.div>

        {/* Executive Photo & Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 sm:p-8 mb-10"
        >
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Executive Photo */}
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-[rgba(218,255,1,0.3)]">
              <img 
                src="https://customer-assets.emergentagent.com/job_05bb89b9-bd12-4211-ba45-1fade7c1a42d/artifacts/lcp93jzw_IMG_20251123_150917_567.webp"
                alt="Leaver Hall - Managing Director, ItzGreen Music"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1">
              <h2
                className="text-white text-xl sm:text-2xl font-bold mb-1"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                LEAVER HALL
              </h2>
              <p
                className="text-[rgb(218,255,1)] text-xs tracking-[0.15em] font-medium mb-4"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                MANAGING DIRECTOR & FOUNDER
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <div className="flex items-center gap-3">
                  <Award size={16} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(161,161,170)]">Managing Director</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield size={16} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(161,161,170)]">Liability Shield Architect</span>
                </div>
                <div className="flex items-center gap-3">
                  <Music size={16} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(161,161,170)]">Executive Strategist</span>
                </div>
                <div className="flex items-center gap-3">
                  <Briefcase size={16} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(161,161,170)]">B2B Sync Infrastructure</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Executive Bio - EXACT SEO OPTIMIZED COPY */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6 mb-10"
        >
          <div className="space-y-5 text-[rgb(200,200,210)] text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            <p>
              <strong className="text-white">Leaver Hall</strong> is the <strong className="text-white">Managing Director and Founder of ItzGreen Music</strong>, a premium B2B sync administration firm and exclusive audio supplier for network television, film, and theatrical trailer campaigns.
            </p>

            <p>
              Operating at the intersection of music production and rights administration, <strong className="text-white">Leaver Hall</strong> built the ItzGreen infrastructure to solve the sync industry's biggest bottleneck: <strong className="text-[rgb(218,255,1)]">clearance friction</strong>. He is the architect of the firm's strict <strong className="text-white">Liability Shield</strong>, a proprietary intake protocol guaranteeing that every asset in the ItzGreen catalog is 100% "One-Stop" cleared, fully indemnified, and legally reconciled via <strong className="text-white">BMI</strong> and <strong className="text-white">The MLC</strong>.
            </p>

            <p>
              As a <strong className="text-white">Managing Director</strong> and executive strategist, <strong className="text-white">Leaver Hall</strong> oversees a specialized roster. Under his direction, <strong className="text-white">ItzGreen Music</strong> supplies edit bays with premium <strong className="text-white">Commercial Lifestyle</strong>, <strong className="text-white">Authentic Cinematic Urban</strong>, <strong className="text-white">Contemporary R&B</strong>, and <strong className="text-white">House/Dance</strong>.
            </p>

            <p>
              Through rigorous B2B vetting and a commitment to <strong className="text-[rgb(218,255,1)]">100% human-produced, zero-AI assets</strong>, <strong className="text-white">Leaver Hall</strong> ensures that when major networks and trailer houses pull from the <strong className="text-white">ItzGreen Supervisor Vault</strong>, they receive elite sonic quality with zero administrative drag.
            </p>
          </div>
        </motion.div>

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* Core Competencies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-10"
        >
          <h2
            className="text-white text-lg font-bold mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            CORE COMPETENCIES
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Shield, title: "Rights Administration", desc: "Master & Publishing reconciliation, PRO registration, one-stop clearance protocols" },
              { icon: Building2, title: "B2B Sync Infrastructure", desc: "End-to-end licensing workflows for film, TV, advertising, and trailer production" },
              { icon: Users, title: "A&R & Artist Development", desc: "Talent scouting, catalog acquisition, and producer administration partnerships" },
              { icon: FileCheck, title: "Liability Shield Protocol", desc: "100% indemnified assets with zero clearance friction for verified network partners" },
              { icon: Globe, title: "Global Distribution", desc: "Broadcast-ready assets deployed across theatrical, streaming, and commercial markets" },
              { icon: CheckCircle, title: "Quality Control", desc: "100% human-produced guarantee with zero AI-generated content in the catalog" },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] rounded-xl p-4 hover:border-[rgba(218,255,1,0.2)] transition-colors"
              >
                <item.icon size={18} className="text-[rgb(218,255,1)] mb-2" />
                <h3 className="text-white text-sm font-semibold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.title}
                </h3>
                <p className="text-[rgb(161,161,170)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[rgba(218,255,1,0.05)] border border-[rgba(218,255,1,0.2)] rounded-xl p-6 text-center"
        >
          <h3
            className="text-white text-lg font-bold mb-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            CONNECT WITH LEAVER HALL
          </h3>
          <p
            className="text-[rgb(161,161,170)] text-sm mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            For licensing inquiries, partnership opportunities, or to discuss your project's sonic needs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:leaver@itzgreenmusic.com"
              className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-bold tracking-wider px-6 py-3 rounded-xl hover:bg-[rgb(166,190,21)] transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              EMAIL DIRECTLY
            </a>
            <Link
              to="/licensing"
              className="inline-flex items-center gap-2 border-2 border-[rgb(218,255,1)] text-[rgb(218,255,1)] text-sm font-bold tracking-wider px-6 py-3 rounded-xl hover:bg-[rgba(218,255,1,0.1)] transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              SUBMIT LICENSING REQUEST
            </Link>
          </div>
        </motion.div>

        {/* Schema.org structured data hint */}
        <div className="hidden">
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Person",
              "name": "Leaver Hall",
              "jobTitle": "Managing Director",
              "worksFor": {
                "@type": "Organization",
                "name": "ItzGreen Music",
                "url": "https://itzgreenmusic.com"
              },
              "description": "Managing Director and Founder of ItzGreen Music, a B2B sync licensing firm specializing in one-stop cleared production music for film, television, and advertising."
            })}
          </script>
        </div>
      </div>
    </main>
  );
};

export default AboutLeaverHallPage;
